import React, { useCallback, useEffect, useState } from 'react';
import { AfterPayBanner, AlertsList, EmptyProductsFallback } from '../../components';
import { useGetCustomerNameListSettings } from '../../components/regalia-ux/hooks';
import {
	BREADCRUMB,
	HELPBUTTON,
	PAGETITLE,
	PAYPALBANNER,
	PRODUCTTILES,
	PROMOMSG
} from '../../constants/rootElements';
import { useFeeAlertInjectionScript } from "../../hooks";
import { useGoogleAnalyticsHandler } from '../../hooks/useGoogleAnalyticsHandler';
import { gaPageLoad } from "../../util/googleAnalytics";
import { isAllSubCatalogProductsEmpty } from '../../util/isAllSubCatalogProductsEmpty';
import { Breadcrumb } from '../breadcrumb';
import { HelpButton } from '../help';
import { TitleBar } from '../productBrowseTitle';
import { ColRegaliaProductTiles } from './colRegaliaProductTiles';

export const ColRegaliaPage = React.memo(({ appData }) => {
	const [pbData, setPbData] = useState({});
	const [catalogData, setCatalogData] = useState({});
	const [breadcrumbData, setBreadcrupmData] = useState([]);
	const [promoMessages, setPromoMessages] = useState([]);
	const [feeAlertRequestData, setFeeAlertRequestData] = useState(null);
	const [googleAnalyticsRequestData, setGoogleAnalyticsRequestData] = useState(null);
    const [currencyCode, setCurrencyCode] = useState(null);

	useFeeAlertInjectionScript(feeAlertRequestData);
	const { selectItemTrackingGASY25, viewModalTrackingGASY25, addToCartTrackingGASY25 } = useGoogleAnalyticsHandler(googleAnalyticsRequestData);

	const ColRegaliaBreadcrumb = Boolean(
		appData?.pagecontentData?.find((data) => data.contentType === 'Navigation')
	);
	const ColRegaliaTitleBar = appData?.pagecontentData?.find(
		(data) => data.contentType === 'ProductBrowseTitle'
	);
	const ProductBrowseProducts = appData?.pagecontentData?.find(
		(data) => data.contentType === 'ProductBrowseProducts'
	);
	const ProductBrowsePromoMsg = Boolean(
		appData?.pagecontentData?.find(
			(data) => data.contentType === 'ProductBrowsePromoMsg'
		)
	);
	const PaypalBannerData = appData?.pagecontentData?.find(
		(data) => data.contentType === 'PaypalId'
	);
	const ColRegaliaFilterTile = appData?.pagecontentData?.find(
		(data) => data.contentType === 'ProductBrowseFiltersBar'
	);
	const customerHasSY2025UX = Object.keys(appData.productBrowseData)
		.filter(key => key !== 'metaData')
		.some(key => appData.productBrowseData[key].customerHasSY2025UX)

	const { customerNameListSettings } = useGetCustomerNameListSettings()
	const isStudentVerificationAvailable = customerNameListSettings && customerNameListSettings.length > 0
	
	const isRegaliaUx = customerHasSY2025UX || isStudentVerificationAvailable

	const getBreadcrumb = useCallback(() => {
		BREADCRUMB.render(<Breadcrumb pbData={breadcrumbData} />);
	}, [breadcrumbData]);

	const getHelpButton = useCallback(() => {
        HELPBUTTON.render(<HelpButton />);
	},[]);

	const getTitleBar = useCallback(() => {
		PAGETITLE.render(
			<TitleBar
				pageContent={ColRegaliaTitleBar}
				affiliationData={appData?.affiliationData}
			/>
		);
	}, []);

	const productTiles = useCallback(() => {
		const isSubCatalogProductsEmpty = isAllSubCatalogProductsEmpty(catalogData)
		const isProductsEmpty = !Boolean(pbData?.products?.length)

		PRODUCTTILES.render(
			isSubCatalogProductsEmpty && isProductsEmpty
				? <EmptyProductsFallback
					productBrowseData={pbData}
				/>
				: <ColRegaliaProductTiles
					pbData={pbData}
					pageContent={ProductBrowseProducts}
					filterData={appData.filterData}
					serviceData={catalogData}
					fieldToCompare={ColRegaliaFilterTile?.filedtocompare}
					isRegaliaUx={isRegaliaUx}
					googleAnalytics={{ 
						selectItemTrackingGASY25, 
						viewModalTrackingGASY25, 
						addToCartTrackingGASY25,
						catalogOSR: googleAnalyticsRequestData?.catalogOsr,
						customerClassCode: appData.affiliationData?.customerClassCode
					}}
				/>
		)
		// eslint-disable-next-line
	}, [catalogData, isRegaliaUx, selectItemTrackingGASY25]);

	const getPromoMsg = useCallback(() => {
		PROMOMSG.render(<AlertsList messages={promoMessages} />);
	}, [promoMessages]);

	const paypalBanner = useCallback(() => {
		PAYPALBANNER.render(<AfterPayBanner currencyCode={currencyCode} />);
	},[PaypalBannerData, currencyCode]);

	useEffect(() => {
		Object.keys(appData.productBrowseData).map((key) => {
			if (key !== 'metaData') {
				const catalog = appData.productBrowseData[key];
				catalog?.productGroups &&
					Object.keys(catalog.productGroups).map(
						(catObj) => {
							const productGroup = catalog.productGroups[catObj];
							const customerNumber = appData.productBrowseData.metaData.customerNumber;
							setFeeAlertRequestData({
								containerSelector: '#promoMsg',
								customerId: customerNumber,
								catalogOsr: catalog.catalogOsr,
							})
							setGoogleAnalyticsRequestData({
								customerNumber,
								publishedCatalogId: catalog.publishedCatalogId,
								sourceCatalogType: catalog.sourceCatalogType,
								catalogOsr: catalog.catalogOsr,
								customerClassCode: appData.affiliationData?.customerClassCode
							});
							setPbData(productGroup);
							const _currencyCode = productGroup?.products?.[0]?.currencyCode;
							_currencyCode && setCurrencyCode(_currencyCode);
							setPromoMessages(catalog.promoMessages);
							setCatalogData(catalog);
							setBreadcrupmData(productGroup);
							return true;
						}
					);
			}
			return true;
		});
	}, [appData.productBrowseData]);

	useEffect(() => {
		if (appData.productBrowseData && Object.keys(appData.productBrowseData).length > 0) {
			gaPageLoad();
		}
	}, [])

	return (
		<>
			{ColRegaliaTitleBar &&
				Object.keys(ColRegaliaTitleBar)?.length > 0 &&
				getTitleBar()}
			{ProductBrowsePromoMsg && promoMessages?.length > 0 && getPromoMsg()}
			{ColRegaliaBreadcrumb && getBreadcrumb()}
			{ProductBrowseProducts &&
				Object.keys(ProductBrowseProducts)?.length > 0 &&
				productTiles()
				}
			{
            getHelpButton()
        	}
			{PaypalBannerData &&
				Object.keys(PaypalBannerData)?.length > 0 &&
				paypalBanner()}
		</>
	);
});
